import {Component, HostBinding, OnDestroy, OnInit} from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

import { Subject, Subscription } from 'rxjs';
import { ConfirmPopupComponent } from 'src/app/components';
import { ReportPageConstant } from 'src/app/constants/reportPage.constants';
import { ReportPageInterface } from 'src/app/interface';
import { FieldValue, Projection } from 'src/app/model';
import { FooterButtonOption, PageNumbers } from 'src/app/model/Enums';
import { Report } from 'src/app/model/report';
import { ReportProvider } from 'src/app/providers';
import {CommonService, ReportService, ToasterService} from 'src/app/services';
import { ValueWarningPopupComponent } from '../../..';
import {routeTransition} from '../../../../../animations/route-transition';

@Component({
  selector: 'app-projection-table',
  templateUrl: './projection-table.component.html',
  styleUrls: ['./projection-table.component.scss'],
  animations: [routeTransition],
})
export class ProjectionTableComponent implements OnInit, OnDestroy, ReportPageInterface {
  @HostBinding('@routeTransition') routeAnimation = true;
  public currentReport: Report;
  private reportSub: Subscription;
  reportPageConst = ReportPageConstant;

  isAddButtonDisabled: boolean = false;

  tableData: any[] = [];

  constructor(
    private reportService: ReportService,
    private reportProvider: ReportProvider,
    private router: Router,
    private modalService: BsModalService,
    private toastr: ToasterService,
    private common: CommonService,
  ) { }

  ngOnInit(): void {
    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;
      if (this.currentReport && this.currentReport.LATEST) {
        this.getProjectionDetails();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.reportSub)
      this.reportSub.unsubscribe();
  }

  /**
   * @description for get projection details
   */
  getProjectionDetails() {
    this.reportProvider.getProjection(this.currentReport.LATEST.ID).subscribe(resp => {
      if (resp.success) {
        this.currentReport.LATEST.PROJECTION_LIST = resp.result;
        this.currentReport.LATEST.PROJECTION_LIST.sort((a, b) => Number(a.YEAR) - Number(b.YEAR));
        this.isAddButtonDisabled = false;
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description for add new projection
   */
  addProjectionDetail() {
    this.isAddButtonDisabled = true;
    this.toastr.info('Please wait a moment!');
    const objProj = new Projection();
    this.reportProvider.addProjection(this.currentReport.LATEST.ID, objProj).subscribe(res => {
      if (res.success) {
        this.getProjectionDetails();
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description for delete particular projection
   * @param index position
   * @param obj projection object
   */
  deleteProjectionDetail(index: number, obj: Projection) {
    let confirmModalRef: BsModalRef;
    confirmModalRef = this.modalService.show(ConfirmPopupComponent, Object.assign({
      initialState: {
        message: 'Do you want to remove Projection?'
      }
    }, { class: 'modal-sm modal-dialog-centered' }));
    const ackSubject = new Subject<boolean>();
    confirmModalRef.content.subject = ackSubject;
    ackSubject.subscribe(value => {
      if (value) {
        this.toastr.info('Please wait a moment!');
        this.reportProvider.deleteProjection(this.currentReport.LATEST.ID, obj.ID).subscribe(res => {
          if (res.success) {
            this.getProjectionDetails();
            this.toastr.success('Projection deleted successfully!');
          }
        }, err => {
          // Error Handling
        });

      }
    });
  }

  /**
   * @description field update API
   * @param fieldName field name which we want to update
   * @param obj projection year which year we want to update
   */
  callUpdateAPI(fieldName: string, obj: Projection) {
    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'PROJECTION';
    objFieldValue.FIELD = fieldName;
    objFieldValue.VALUE = obj[fieldName] || 0;
    this.reportProvider.updateReportField(obj.ID, objFieldValue).subscribe(res => {
      if (!res.success) {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description change auto/manual calc
   */
   changeAutomaticallyManually() {
    this.currentReport.LATEST.PROJECTIONS_ADVANCED = !this.currentReport.LATEST.PROJECTIONS_ADVANCED;
    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'REPORT_VERSION';
    objFieldValue.FIELD = 'PROJECTIONS_ADVANCED';
    objFieldValue.VALUE = this.currentReport.LATEST.PROJECTIONS_ADVANCED.toString();
    this.reportProvider.updateReportField(this.currentReport.LATEST.ID, objFieldValue).subscribe(res => {
      if (res.success) {
        this.getProjectionDetails();
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  showLargeValueWarningModel(value: number = 0, isRevenue: boolean = false) {
    const enteredAmount = Number(value);
    if (isRevenue) {
      if (enteredAmount > 10000000) {
        this.modalService.show(ValueWarningPopupComponent, Object.assign({
          initialState: {
            title: 'Warning - Projection Value Too Large',
            message: 'Please note that your Projections value cannot exceed 10,000,000.'
          }
        }, { class: 'modal-lg modal-dialog-centered' }));
      }
    } else {
      if (enteredAmount > 300) {
        this.modalService.show(ValueWarningPopupComponent, Object.assign({
          initialState: {
            title: 'Warning - Projection Value Too Large',
            message: 'Please note that your Projections value cannot exceed 300.'
          }
        }, { class: 'modal-lg modal-dialog-centered' }));
      }
    }
  }

  /**
   * @description function of interface for validation
   * @returns form valid or not
   */
  isFormValid(): boolean {
    return true;
  }

  /**
   * @description function of interface for skip functionality
   * @returns form can be skippable or not
   */
  canBeSkipped(): boolean {
    return true;
  }

  /**
   * @description report footer button click options
   * @param buttonOption which button clicked by user
   */
  footerButtonClick(buttonOption: number) {
    if (buttonOption == FooterButtonOption.NEXT_CLICK) {
      this.reportService.updateMaxStage(PageNumbers.PROJECTION_TABLE);
      this.router.navigateByUrl(`/report/${this.currentReport.ID}/assets-liabilities`);
    } else if (buttonOption == FooterButtonOption.BACK_CLICK) {
      this.router.navigateByUrl(`/report/${this.currentReport.ID}/adjustments`);
    }
  }

  /** check max cash flow value */
  get isCashFlowOverFifteenMillion(): boolean{
    let calc = 0;
    this.currentReport?.LATEST?.PROJECTION_LIST.forEach(project => {
      calc += project.PROJECTED_SDE;
    });
    if(calc > 0){
      return ((calc / this.currentReport.LATEST.PROJECTION_LIST.length) > this.common.maxCashflow);
    }else{
      return false;
    }
  }
}
